/**
 * Expand Tile Base
 */
export class GMXExpandTileBase extends HTMLElement {
	tileExpandBtn: HTMLButtonElement
	tileExpandedCloseBtn: HTMLButtonElement
	collapsedContentContainerEl: HTMLElement
	expandContentContainerEl: HTMLElement
	expandedContentEl: HTMLElement
	intersectionObserver: IntersectionObserver
	expandOverlayEl: HTMLElement
	bodyMaskEl: HTMLElement

	private boundExpandBtnClickHandler = this.handleExpandBtnClicked.bind(this)
	private boundExpandCloseBtnClickHandler = this.handleExpandContentCloseBtnClicked.bind(this)
	private boundHandleObservationCallback = this.handleObservationCallback.bind(this)
	private boundHandleExpandTileTransitionEndCallback = this._handleExpandTileTransitionEnd.bind(this)

	connectedCallback(): void {
		this.expandContentContainerEl = this.querySelector('.expanded-content-container')
		this.collapsedContentContainerEl = this.querySelector('.collapsed-content-container')
		this.tileExpandBtn = this.querySelector('.tile-expand-btn')

		this.expandedContentEl = this.querySelector('.expanded-content')

		this.collapsedContentContainerEl?.addEventListener('click', this.boundExpandBtnClickHandler)

		this.tileExpandedCloseBtn = this.expandContentContainerEl?.querySelector('.close-btn')

		if (this.tileExpandedCloseBtn) {
			this.tileExpandedCloseBtn.addEventListener('click', this.boundExpandCloseBtnClickHandler)
		}

		this.intersectionObserver = new IntersectionObserver(this.boundHandleObservationCallback, {
			root: null,
			rootMargin: '0px',
			threshold: .5,
		});
		this.intersectionObserver.observe(this);
	}

	disconnectedCallback(): void {
		this.collapsedContentContainerEl?.removeEventListener('click', this.boundExpandBtnClickHandler)

		if (this.tileExpandedCloseBtn) {
			this.tileExpandedCloseBtn.removeEventListener('click', this.boundExpandCloseBtnClickHandler)
		}

		if (this.intersectionObserver) {
			this.intersectionObserver.unobserve(this);
		}
	}

	/**
	 * Callback for when the tile is clicked to open
	 */
	handleExpandBtnClicked(e: Event): void {
		const target = e.target as HTMLElement

		if (
			!(target && (target.classList.contains('gmx-controls-container') || target.closest('.gmx-controls-container')))
			&& !(target && (target.matches('[data-toast-uri],[data-toast-msg]') || target.closest('[data-toast-uri],[data-toast-msg]')))
		) {
			/* First, immediately load the content if we somehow reached this point and haven't done so yet */
			if (!this.expandedContentEl.classList.contains('loaded')) {
				this.loadContent();
			}

			/* Next dispatch an event to notify that the expand overlay is opening */
			const newNotifyEvent = new CustomEvent('gmxTileOpening', {
				detail: {
					tileType: 'expand',
				}, // todo: do we need an id or something here ???
			})
			this.dispatchEvent(newNotifyEvent)

			/* Start by setting the body CSS so that you cannot scroll the background when expand overlay is open */
			document.body.classList.add('scroll-disabled')

			if (this.bodyMaskEl) {
				this.bodyMaskEl.classList.add('show')
			}

			if (this.expandOverlayEl) {
				this.expandOverlayEl.setAttribute('data-show', '')
			}

			this.handleOverlaySize()
		}
	}

	private _handleExpandTileTransitionEnd(): void {
		this.expandContentContainerEl.classList.add('fix-to-expand-position')
		this.expandContentContainerEl.style.transform = ''
	}

	handleOverlaySize(): void {
		const rect = this.getBoundingClientRect()

		if (this.isMobileORTablet()) {
			this.expandContentContainerEl.style.transform = `translate3d(-${rect.left}px, ${rect.top * -1}px, 0)`
		} else {
			this.expandContentContainerEl.style.transform = `translate3d(0, ${rect.top * -1 + 16}px, 0)`
		}

		this.expandContentContainerEl.addEventListener('transitionend', this.boundHandleExpandTileTransitionEndCallback, {once: true})

		this.expandContentContainerEl.classList.add('expanded')
		this.classList.add('expanded')
	}

	/**
	 * Callback for when the expand container close button is clicked
	 */
	handleExpandContentCloseBtnClicked(): void {
		this.expandContentContainerEl.removeEventListener('transitionend', this.boundHandleExpandTileTransitionEndCallback)
		document.body.classList.remove('scroll-disabled')
		this.expandContentContainerEl.style.transform = ''

		/* Next dispatch an event to notify that the expand overlay is opening */
		const newNotifyEvent = new CustomEvent('gmxTileClosing', {
			detail: {
				tileType: 'expand',
			}, // todo: do we need an id or something here ???
		})
		this.dispatchEvent(newNotifyEvent)

		this.classList.remove('expanded')

		if (this.bodyMaskEl) {
			this.bodyMaskEl.classList.remove('show')
		}

		this.expandContentContainerEl.classList.remove('fix-to-expand-position')
		this.expandContentContainerEl.style.transform = ''

		this.expandContentContainerEl.classList.remove('expanded')

		if (this.expandOverlayEl) {
			this.expandOverlayEl.removeAttribute('data-show')
		}
	}

	handleObservationCallback(): void {
		this.loadContent()

		this.intersectionObserver.disconnect()
	}

	/**
	 * Loads expanded tile content
	 */
	private async loadContent(): Promise<void> {
		const partialPathUrl = this.getAttribute('data-expanded-content-path')

		if (partialPathUrl) {
			const targetOverlayEl = this.querySelector(`[id='${partialPathUrl}']`)

			if (!targetOverlayEl) {
				this.expandOverlayEl = document.createElement('gmx-overlay')
				this.expandOverlayEl.setAttribute('id', this.getAttribute('data-expanded-content-path'))
				this.expandOverlayEl.setAttribute('data-overlay-type', 'expand-from-tile')

				const mutationObserver = new MutationObserver((mutationList) => {
					for (const mutation of mutationList) {
						if (mutation.type === 'attributes' && this.expandOverlayEl.hasAttribute('data-overlay-loaded')) {
							this.tileExpandedCloseBtn = this.expandOverlayEl.querySelector('.close-btn')

							if (this.tileExpandedCloseBtn) {
								this.tileExpandedCloseBtn.addEventListener('click', this.boundExpandCloseBtnClickHandler)
							}
							mutationObserver.disconnect()

							this.onOverlayLoaded()
						}
					}
				})
				mutationObserver.observe(this.expandOverlayEl, { attributes: true })

				this.expandedContentEl.appendChild(this.expandOverlayEl)
			}
		}

		const bodyMaskEl = document.body.querySelector('gmx-body-mask:not([data-associated-overlay-id])') as HTMLElement

		if (bodyMaskEl) {
			this.bodyMaskEl = bodyMaskEl
		} else {
			this.bodyMaskEl = document.createElement('gmx-body-mask')
			document.body.appendChild(this.bodyMaskEl)
		}
	}

	/**
	 * Check if the screen is mobile size
	 */
	isMobileORTablet(): boolean {
		return !window.matchMedia('screen and (min-width: 900px)').matches
	}

	/**
	 * Programmatically open the expand tile — used by the deeplink singleton and any
	 * other external caller that needs to open the tile without a click event.
	 */
	public open(): void {
		if (!this.expandedContentEl?.classList.contains('loaded')) {
			this.loadContent()
		}

		const newNotifyEvent = new CustomEvent('gmxTileOpening', {
			detail: { tileType: 'expand' },
		})
		this.dispatchEvent(newNotifyEvent)

		document.body.classList.add('scroll-disabled')

		if (this.bodyMaskEl) {
			this.bodyMaskEl.classList.add('show')
		}

		// expandOverlayEl may not be set when content was pre-rendered in the HTML
		if (!this.expandOverlayEl) {
			this.expandOverlayEl = this.querySelector('gmx-overlay') as HTMLElement
		}

		if (this.expandOverlayEl) {
			this.expandOverlayEl.setAttribute('data-show', '')
		}

		this.handleOverlaySize()
	}

	/**
	 * Method to be overloaded by inherited components (ie, Carousel Tile)
	 */
	onOverlayLoaded(): void {
		/* Implementation to be done by descendant components */
		/* todo:  maybe create an interface??? */
	}
}

if (!customElements.get('gmx-expand-tile-base')) {
	customElements.define('gmx-expand-tile-base', GMXExpandTileBase)
}
